import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import "./GrowthChart.css";
import { useNavigate } from "react-router-dom";

// Register the necessary components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

const GrowthChartDoctor = ({ childId }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = window.innerWidth <= 720;
  const token = localStorage.getItem("s2stoken");
  const navigate = useNavigate();

  // Fetch chart data for the specified child
  useEffect(() => {
    if (!childId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get_chart_doctor_view?patient_id=${childId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            credentials: "include",
          }
        );
        console.log(response);
        const data = await response.json();
        // alert(JSON.stringify(data, null, 2));
        console.log("API Response:", data); // Log the response for debugging
        if (!response.ok || data.success === false) {
          throw new Error(data.message || "Failed to fetch chart data");
        }
        if (data.success) {
          // Check if all head_circumference values are null
          const allHeadCircumferenceNull = Object.values(data.child_data).every(
            (dataPoint) => dataPoint.head_circumference === null
          );
          // Set the chart data, also indicating if head circumference chart should be displayed
          setChartData({
            ...data,
            allHeadCircumferenceNull, // Store the check result in the state to conditionally render the chart
          });
          setLoading(false);
        } else {
          setError(data.message || "Failed to fetch chart data");
          setLoading(false);
        }
      } catch (err) {
        if (err?.message === "Token has expired") {
          localStorage.removeItem("s2stoken");
          navigate("/");
        }
        console.error("Fetch Error:", err); // Log any fetch errors
        setError("Failed to fetch chart data");
        setLoading(false);
      }
    };

    fetchData();
  }, [childId]);

  console.log(chartData);
  const createChartData = (childData, percentileData, label) => {
    const labels = percentileData.map((d) => d.Month);
    const yAxisLabel =
      label === "weight"
        ? "Weight (kg)"
        : label === "height"
        ? "Length (cm)"
        : "Head Circumference (cm)";

    // Filter out unwanted percentiles
    const validPercentiles = ["P1", "P3", "P25", "P50", "P75", "P97", "P99"];
    // const percentileColumns = Object.keys(percentileData[0]).filter(
    //   (key) => key.startsWith("P") && !validPercentiles.includes(key)
    // );
    const colorOptions = [
      "#1E3A8A",
      "#0E7490",
      "#D97706",
      "#166534",
      "#800020",
    ];
    const percentileDatasets = validPercentiles.map((column, index) => ({
      label: `Percentile (${column})`,
      data: percentileData.map((d) => ({ x: d.Month, y: d[column] })),
      // borderColor: colorOptions[index % colorOptions.length],
      borderColor: "#E57373",
      borderWidth: isMobile ? 0.8 : 1.2,
      fill: false,
      pointRadius: 0,
      lineTension: 0,
      borderDash: [],
    }));

    // Convert childData into multiple data points
    const childDataPoints = Object.entries(childData)
      .flatMap(([age, records]) =>
        records.map((values) => ({
          x: parseFloat(age),
          y: values[label],
        }))
      )
      .filter((point) => point.y !== 0)
      .sort((a, b) => a.x - b.x);
    console.log(childDataPoints);
    const childDataLine = {
      label: "Child Data",
      data: childDataPoints,
      borderColor: "black",
      backgroundColor: "black",
      fill: false,
      pointRadius: isMobile ? 1.6 : 2.2,
      borderWidth: isMobile ? 1.6 : 2,
      showLine: true, // Ensure the line connects points
      lineTension: 0.2, // Optional: Adjust for smooth curve
      pointHitRadius: 10,
    };

    return {
      labels,
      datasets: [childDataLine, ...percentileDatasets],
      yAxisLabel, // Pass the label for use in tooltips
    };
  };
  const createWeightLengthChartData = (
    child_weight_length_data,
    weight_length_data
  ) => {
    const childDataArray = Object.entries(child_weight_length_data)
      .flatMap(([key, valueArray]) =>
        valueArray.map((weight) => ({
          length: parseFloat(key),
          weight: parseFloat(weight),
        }))
      )
      .filter((data) => data.length !== 0 && data.weight !== 0);

    let extendedWeightData = [...weight_length_data];
    // Assuming the normative data objects use "Age (months)" as key for length.
    if (extendedWeightData[0]["Age (months)"] > 0) {
      // Create an extrapolated point at x = 0 using the first point's values.
      const firstPoint = extendedWeightData[0];
      extendedWeightData.unshift({
        ...firstPoint,
        "Age (months)": 0,
      });
    }
    const lastPoint = extendedWeightData[extendedWeightData.length - 1];
    if (lastPoint["Age (months)"] < 102) {
      // Create an extrapolated point at x = 96 using the last point's values.
      extendedWeightData.push({
        ...lastPoint,
        "Age (months)": 102,
      });
    }
    console.log(childDataArray);
    // Define the percentiles we want to show
    const validPercentiles = ["P1", "P3", "P25", "P50", "P75", "P97", "P99"];

    // Create a dataset for each percentile curve
    const percentileDatasets = validPercentiles.map((percentile) => ({
      label: `Percentile (${percentile})`,
      data: extendedWeightData.map((d) => ({
        x: d["Age (months)"], // x-axis: length (cm)
        y: d[percentile], // y-axis: weight (kg) for that percentile
      })),
      borderColor: "#E57373",
      borderWidth: isMobile ? 0.8 : 1.2,
      fill: false,
      pointRadius: 0,
      lineTension: 0,
      borderDash: [],
    }));

    // Create a dataset for the child's measured data
    const childDataset = {
      label: "Child Data",
      data: childDataArray.map((d) => ({
        x: d.length, // length (cm)
        y: d.weight, // weight (kg)
      })),
      borderColor: "black",
      backgroundColor: "black",
      fill: false,
      pointRadius: isMobile ? 1.6 : 2.2,
      borderWidth: isMobile ? 1.6 : 2,
      showLine: true,
      lineTension: 0.2,
      pointHitRadius: 10,
    };

    // Use the lengths from normative data as labels (if needed for tooltips)
    const labels = extendedWeightData.map((d) => d.Length);

    return {
      labels,
      datasets: [childDataset, ...percentileDatasets],
      xAxisLabel: "Length (cm)",
      yAxisLabel: "Weight (kg)",
    };
  };

  const interpolatePercentile = (age, value, percentileData) => {
    for (let i = 0; i < percentileData.length - 1; i++) {
      const point1 = percentileData[i];
      const point2 = percentileData[i + 1];

      if (point1.Month <= age && age <= point2.Month) {
        const percentiles = Object.keys(point1).filter((k) =>
          k.startsWith("P")
        );

        for (let j = 0; j < percentiles.length - 1; j++) {
          const p1Value1 = point1[percentiles[j]];
          const p1Value2 = point2[percentiles[j]];
          const p2Value1 = point1[percentiles[j + 1]];
          const p2Value2 = point2[percentiles[j + 1]];

          if (p1Value1 <= value && value <= p2Value1) {
            const x1 = point1.Month;
            const x2 = point2.Month;
            const y1 = p1Value1;
            const y2 = p2Value1;

            const percentileStart = parseFloat(percentiles[j].substring(1));
            const percentileEnd = parseFloat(percentiles[j + 1].substring(1));
            const t = (value - y1) / (y2 - y1);
            const interpolatedPercentile =
              percentileStart + t * (percentileEnd - percentileStart);

            return `P${interpolatedPercentile.toFixed(1)}`;
          }
        }
      }
    }

    return "N/A";
  };
  const interpolatePercentileWL = (x, y, normativeData) => {
    // normativeData: Array of objects with keys: "Age (months)", "P1", "P3", ... etc.
    for (let i = 0; i < normativeData.length - 1; i++) {
      const point1 = normativeData[i];
      const point2 = normativeData[i + 1];

      // Check if child's x falls between these two normative data points based on "Age (months)"
      if (point1["Age (months)"] <= x && x <= point2["Age (months)"]) {
        // Get percentile keys (those starting with "P") and sort them numerically
        const percentiles = Object.keys(point1)
          .filter((k) => k.startsWith("P"))
          .sort(
            (a, b) => parseFloat(a.substring(1)) - parseFloat(b.substring(1))
          );

        // Loop through each consecutive pair of percentile keys
        for (let j = 0; j < percentiles.length - 1; j++) {
          const key1 = percentiles[j];
          const key2 = percentiles[j + 1];

          // Interpolate the normative weight at child's x for both percentile levels
          const interp1 =
            point1[key1] +
            ((point2[key1] - point1[key1]) * (x - point1["Age (months)"])) /
              (point2["Age (months)"] - point1["Age (months)"]);
          const interp2 =
            point1[key2] +
            ((point2[key2] - point1[key2]) * (x - point1["Age (months)"])) /
              (point2["Age (months)"] - point1["Age (months)"]);

          // If the child's weight falls between these two interpolated weights,
          // we calculate the fractional position between these percentiles.
          if (interp1 <= y && y <= interp2) {
            const p1 = parseFloat(key1.substring(1));
            const p2 = parseFloat(key2.substring(1));
            const t = (y - interp1) / (interp2 - interp1);
            const interpolatedPercentile = p1 + t * (p2 - p1);
            return `P${interpolatedPercentile.toFixed(1)}`;
          }
        }
      }
    }
    return "N/A";
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const {
    child_data,
    weight_data,
    height_data,
    head_circumference_data,
    child_weight_length_data,
    weight_length_data,
  } = chartData;

  const weightChartData = createChartData(child_data, weight_data, "weight");
  const heightChartData = createChartData(child_data, height_data, "height");
  const headCircumferenceChartData = createChartData(
    child_data,
    head_circumference_data,
    "head_circumference"
  );
  const weightLengthChartData = createWeightLengthChartData(
    child_weight_length_data,
    weight_length_data
  );

  const weightLengthChartOptions = {
    responsive: true,
    devicePixelRatio: window.devicePixelRatio > 1 ? 2 : 1,
    plugins: {
      legend: {
        position: "top",
        labels: {
          filter: (item) =>
            item.text === "Percentile" || item.text === "Child Data",
          usePointStyle: true,
          generateLabels: (chart) => {
            const data = chart.data;
            const labels = data.datasets.map((dataset, i) => {
              if (dataset.label && dataset.label.startsWith("Percentile")) {
                return {
                  text: "Percentile",
                  fillStyle: dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  pointStyle: "line",
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i,
                  lineWidth: dataset.borderWidth * 2,
                };
              } else if (dataset.label === "Child Data") {
                return {
                  text: dataset.label,
                  fillStyle: dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  pointStyle: "circle",
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i,
                  pointRadius: isMobile ? 1 : 1.5,
                };
              }
              return {
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                pointStyle: dataset.pointStyle || "line",
                hidden: !chart.isDatasetVisible(i),
                datasetIndex: i,
              };
            });
            return labels.filter(
              (label, index, self) =>
                index === self.findIndex((l) => l.text === label.text)
            );
          },
          font: {
            size: isMobile ? 10 : 14,
          },
        },
      },
      tooltip: {
        mode: "nearest",
        intersect: false,
        callbacks: {
          title: () => "", // Hide the title
          label: (context) => {
            const datasetLabel = context.dataset.label || "";
            const xAxisValue = context.parsed.x;
            const yAxisValue = context.parsed.y;

            if (datasetLabel === "Child Data") {
              // Use weight_length_data as normative data for interpolation
              const percentile = interpolatePercentileWL(
                xAxisValue,
                yAxisValue,
                weight_length_data
              );
              return `Child Data: (${xAxisValue} cm, ${yAxisValue} kg) - Percentile: ${percentile}`;
            } else if (datasetLabel.startsWith("Percentile")) {
              return `${datasetLabel}: (${xAxisValue} cm, ${yAxisValue} kg)`;
            }
            return `${datasetLabel}: (${xAxisValue} cm, ${yAxisValue} kg)`;
          },
          labelColor: (context) => {
            const backgroundColor = context.dataset.borderColor;
            return {
              borderColor: backgroundColor,
              backgroundColor: backgroundColor,
            };
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        position: "bottom",
        min: 45,
        max: 102,
        grid: {
          color: "#C0C0C0",
        },
        title: {
          display: !isMobile,
          text: weightLengthChartData.xAxisLabel,
          font: {
            size: 14,
            weight: "bold",
          },
        },
        ticks: {
          stepSize: 5,
          font: {
            size: isMobile ? 8 : 12,
          },
        },
      },
      y: {
        min: 0,
        max: 20,
        grid: {
          color: "#C0C0C0",
        },
        title: {
          display: !isMobile,
          text: weightLengthChartData.yAxisLabel,
          font: {
            size: 14,
            weight: "bold",
          },
        },
        ticks: {
          stepSize: 4,
          font: {
            size: isMobile ? 8 : 12,
          },
        },
      },
    },
  };

  const chartOptions = (yAxisLabel, yAxisRange, xAxisRange) => {
    return {
      responsive: true,
      devicePixelRatio: window.devicePixelRatio > 1 ? 2 : 1,
      plugins: {
        legend: {
          display: true,
          labels: {
            filter: (item) =>
              item.text === "Percentile" || item.text === "Child Data",
            usePointStyle: true,
            generateLabels: (chart) => {
              const data = chart.data;
              const labels = data.datasets.map((dataset, i) => {
                if (dataset.label.startsWith("Percentile")) {
                  return {
                    text: "Percentile",
                    fillStyle: dataset.borderColor,
                    strokeStyle: dataset.borderColor,
                    pointStyle: "line",
                    hidden: !chart.isDatasetVisible(i),
                    datasetIndex: i,
                    lineWidth: dataset.borderWidth * 2,
                  };
                } else if (dataset.label === "Child Data") {
                  return {
                    text: dataset.label,
                    fillStyle: dataset.borderColor,
                    strokeStyle: dataset.borderColor,
                    pointStyle: "circle",
                    hidden: !chart.isDatasetVisible(i),
                    datasetIndex: i,
                    pointRadius: isMobile ? 1 : 2,
                  };
                }
                return {
                  text: dataset.label,
                  fillStyle: dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  pointStyle: dataset.pointStyle || "line",
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i,
                };
              });
              return labels.filter(
                (label, index, self) =>
                  index === self.findIndex((l) => l.text === label.text)
              );
            },
            font: {
              size: isMobile ? 10 : 14,
            },
          },
        },
        tooltip: {
          mode: "nearest",
          intersect: false,
          filter: function (tooltipItem) {
            const datasetLabel = tooltipItem.dataset.label;

            // If the hovered dataset is 'Child Data', always show its tooltip
            if (datasetLabel === "Child Data") {
              return true;
            }

            // Check if 'Child Data' or 'Predicted Value' exists at the same point
            const childDataset = tooltipItem.chart.data.datasets.find(
              (d) => d.label === "Child Data"
            );
            // const predictedDataset = tooltipItem.chart.data.datasets.find(d => d.label === 'Predicted Value');

            const childPointExists = childDataset
              ? childDataset.data.some(
                  (point) =>
                    point.x === tooltipItem.parsed.x &&
                    point.y === tooltipItem.parsed.y
                )
              : false;

            // const predictedPointExists = predictedDataset ? predictedDataset.data.some(point =>
            //   point.x === tooltipItem.parsed.x && point.y === tooltipItem.parsed.y
            // ) : false;

            // If 'Child Data'exists at this point, hide other tooltips
            if (childPointExists) {
              return false;
            }

            // Show tooltip for other datasets if no overlapping 'Child Data' or 'Predicted Value' point
            return true;
          },
          callbacks: {
            title: () => "", // Hide the title
            label: (context) => {
              const label = context.dataset.label || "";
              const yAxisValue = context.parsed.y;
              const xAxisValue = context.parsed.x;

              // Determine which data to use (weight, height, or head circumference)
              let percentile = "N/A";
              // Check dataset label to determine which data to pass
              if (context.chart.options.scales.y.title.text === "Weight (kg)") {
                percentile = interpolatePercentile(
                  xAxisValue,
                  yAxisValue,
                  weight_data
                );
              } else if (
                context.chart.options.scales.y.title.text === "Length (cm)"
              ) {
                percentile = interpolatePercentile(
                  xAxisValue,
                  yAxisValue,
                  height_data
                );
              } else if (
                context.chart.options.scales.y.title.text ===
                "Head Circumference (cm)"
              ) {
                percentile = interpolatePercentile(
                  xAxisValue,
                  yAxisValue,
                  head_circumference_data
                );
              }

              if (label.startsWith("Percentile")) {
                const percentileMatch = label.match(/\(P\d+\)/);
                const percentile = percentileMatch
                  ? percentileMatch[0].replace(/[()]/g, "")
                  : "N/A";
                return `(${percentile}, ${xAxisValue}, ${yAxisValue})`;
              } else if (label === "Child Data") {
                return `(${percentile}, ${xAxisValue}, ${yAxisValue})`;
              }
              return `${label}: (${xAxisValue}, ${yAxisValue})`;
            },
            labelColor: (context) => {
              const label = context.dataset.label;
              let backgroundColor = context.dataset.borderColor;
              return {
                borderColor: backgroundColor,
                backgroundColor: backgroundColor,
              };
            },
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          min: 0,
          max: xAxisRange[1],
          grid: {
            color: "#C0C0C0",
          },
          title: {
            display: !isMobile,
            text: "Age (in months)",
            font: {
              size: 16,
              weight: "bold",
            },
          },
          ticks: {
            stepSize: 4,
            font: {
              size: isMobile ? 8 : 12,
            },
          },
        },
        y: {
          min: yAxisRange[0],
          max: yAxisRange[1],
          grid: {
            color: "#C0C0C0",
          },
          title: {
            display: !isMobile,
            text: yAxisLabel,
            font: {
              size: 16,
              weight: "bold",
            },
          },
          ticks: {
            font: {
              size: isMobile ? 8 : 12,
            },
          },
        },
      },
    };
  };

  return (
    <div className="growth-chart-container">
      <h1>Child Growth Percentile Charts</h1>
      <h2>Weight for Age Chart</h2>
      {isMobile && (
        <div className="chart-labels">
          <span>X-axis - (Age in months)</span>
          <span>Y-axis - (Weight in kg)</span>
        </div>
      )}
      <Line
        id="weight-chart"
        data={weightChartData}
        options={chartOptions(weightChartData.yAxisLabel, [2, 20], [0, 36])}
      />
      <h2>Length for Age Chart </h2>
      {isMobile && (
        <div className="chart-labels">
          <span>X-axis (Age in months)</span>
          <span>Y-axis (Length in cm)</span>
        </div>
      )}
      <Line
        id="height-chart"
        data={heightChartData}
        options={chartOptions(heightChartData.yAxisLabel, [42, 110], [0, 36])}
      />
      {!chartData.allHeadCircumferenceNull && (
        <>
          <h2>Head Circumference for Age Chart</h2>
          {isMobile && (
            <div className="chart-labels">
              <span>X-axis (Age in months)</span>
              <span>Y-axis (Head Circumference in cm)</span>
            </div>
          )}
          <Line
            id="head-circ-chart"
            data={headCircumferenceChartData}
            options={chartOptions(
              headCircumferenceChartData.yAxisLabel,
              [30, 54],
              [0, 36]
            )}
          />
        </>
      )}
      <h2>Weight for Length Chart</h2>
      {isMobile && (
        <div className="chart-labels">
          <span>X-axis (Length in cm)</span>
          <span>Y-axis (Weight in kg)</span>
        </div>
      )}
      <Line
        id="weight-height-chart"
        data={weightLengthChartData}
        options={weightLengthChartOptions}
      />
    </div>
  );
};

export default GrowthChartDoctor;
